"use client"

import { uiBuilderConfig } from "@/(admin)/modules/ui-builder/config/uiBuilderConfig"
import { Render } from "@measured/puck"
import dayjs from "dayjs"
import Image from "next/image"
import { useEffect, useState } from "react"
import NotFound from "../app/[locale]/not-found"
import { tryParse } from "../lib/config/lib/helper"
import { TagData } from "../lib/config/types"
import { DefaultImage } from "../lib/external-components"
import { getPostByCategories } from "../lib/navigation-services"
import { generateImagePath } from "../lib/utils"
import "../styles/content-wrapper.css"
import BreadCrumb, { BreadCrumbItem } from "./breadcrumb"
import NormalContent from "./normal-content"
import TableOfContents from "./table-of-content"
import { Spinner } from "./ui/loading"

interface PageData {
  id: number
  attributes: {
    slug: string
    id: string
    content: string
    blockContent: any
    name: string
    categories?: {
      data?: Array<{
        attributes?: {
          name?: string
          slug?: string
        }
      }>
    }
    createdAt?: string
    updatedAt?: string
    publishedAt?: string
    locale?: string
    image?: {
      data?: {
        attributes?: {
          url?: string
        }
      }
    }
    tags?: {
      data?: TagData[]
    }
    excerpt: string
  }
}
const extractTableOfContents = (content: string) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(content, "text/html")
  const headings = Array.from(doc.getElementsByTagName("h1"))

  return headings.map((heading, index) => {
    const id = `section-${index}`
    heading.id = id
    return {
      id,
      title: "",
    }
  })
}

interface PageRenderProps {
  pageData?: PageData[]
  hasBreadcrumb?: boolean
  isTemplate?: boolean
  dict?: any
  base_url?: string
}

const PageRender = ({ pageData, hasBreadcrumb, isTemplate, dict, base_url }: PageRenderProps) => {
  const [loading, setLoading] = useState(true)
  const [uiBuilderData, setUiBuilderData] = useState<any>(null)
  const [breadcrumbData, setBreadcrumbData] = useState()
  const [normalContent, setNormalContent] = useState<string>()
  const [breadcrumbPostDetail, setBreadcrumbPostDetail] = useState<any>()
  const [breadcrumbCategoryPost, setBreadcrumbCategoryPost] = useState<any>()
  const [breadcrumbCategoryPostSlug, setBreadcrumbCategoryPostSlug] = useState<any>()
  const [titleBlog, setTitleBlog] = useState<string>("")
  const [updatedAtBlog, setUpdatedAtBlog] = useState<string>("")
  const [dataRelatedPost, setDataRelatedPost] = useState<PageData[]>()
  const [tag, setTag] = useState<string>()
  const [excerpt, setExcerpt] = useState<string>()
  const slug = pageData?.[0]?.attributes?.slug
  const [tableOfContents, setTableOfContents] = useState<Array<{ id: string; title: string }>>([])
  const [imageUrl, setImageUrl] = useState<string>()

  const fetchPostDataByCategories = async (slug: string) => {
    const response = await getPostByCategories(slug)
    return response
  }

  useEffect(() => {
    const fetchData = async () => {
      if (pageData) {
        setLoading(true)
        let parsedData = null
        if (isTemplate) {
          parsedData = tryParse(pageData?.[0]?.attributes?.content ?? "")
        } else {
          parsedData = tryParse(pageData?.[0]?.attributes?.blockContent?.data?.attributes?.content ?? "")
        }

        const postNormalContent = pageData?.[0]?.attributes?.content
        const getBreadcrumbPostDetail = pageData?.[0]?.attributes?.name
        const categoryData = pageData?.[0]?.attributes?.categories?.data?.[0]?.attributes
        const getBreadcrumbCategoryPost = categoryData?.name
        const getBreadcrumbCategoryPostSlug = categoryData?.slug
        const getTitleBlog = pageData?.[0]?.attributes?.name ?? ""
        const getUpdateDateBlog = pageData?.[0]?.attributes?.updatedAt ?? ""
        const getTag = pageData?.[0]?.attributes?.tags?.data?.[0]?.attributes?.name
        const getExcerpt = pageData?.[0]?.attributes?.excerpt
        const getImageUrl = pageData?.[0]?.attributes?.image?.data?.attributes?.url
        if (postNormalContent) {
          const toc = extractTableOfContents(postNormalContent)
          setTableOfContents(toc)
          const response = await fetchPostDataByCategories(
            pageData?.[0]?.attributes?.categories?.data?.[0]?.attributes?.slug ?? "",
          )
          if (response.length > 0) {
            setDataRelatedPost(response)
          }
          // const newContent = replaceImageUrls(postNormalContent)

          setNormalContent(postNormalContent)
          setBreadcrumbPostDetail(getBreadcrumbPostDetail)
          setBreadcrumbCategoryPost(getBreadcrumbCategoryPost)
          setBreadcrumbCategoryPostSlug(getBreadcrumbCategoryPostSlug)
        }

        if (parsedData) {
          setUiBuilderData(parsedData)
        }

        if (hasBreadcrumb) {
          const getBreadCrumbs = parsedData && parsedData?.root.props.breadcrumb

          setBreadcrumbData(getBreadCrumbs)
        }

        if (getTitleBlog && getUpdateDateBlog) {
          setTitleBlog(getTitleBlog)
          setUpdatedAtBlog(getUpdateDateBlog)
        }
        if (getTag) {
          setTag(getTag)
        }
        if (getExcerpt) {
          setExcerpt(getExcerpt)
        }
        if (getImageUrl) {
          setImageUrl(getImageUrl)
        }
        setLoading(false)
      }
    }

    fetchData()
  }, [pageData])

  if (loading) return <Spinner />

  const configBreadCrum = [
    {
      title: breadcrumbCategoryPost,
      link: `/${breadcrumbCategoryPostSlug}`,
    },
    {
      title: breadcrumbPostDetail,
      link: "#",
    },
  ]

  const checkContent = () => {
    {
      if (uiBuilderData) {
        const titleCurrentPage = uiBuilderData?.root?.props?.title
        const customBreadcrumbs = uiBuilderData?.root?.props?.breadcrumb
        const hasCustomBreadcrumbs = customBreadcrumbs?.length > 0

        const breadcrumbItems =
          customBreadcrumbs?.map((item: BreadCrumbItem) => ({
            title: item?.title,
            link: item?.link ?? "#",
          })) ?? []

        const currentPageLink = hasCustomBreadcrumbs
          ? `${breadcrumbItems.map((item: BreadCrumbItem) => item.link).join("/")}/${slug}`
          : `/${slug}`

        const breadcrumbConfig = [
          ...breadcrumbItems,
          {
            title: titleCurrentPage,
            link: currentPageLink,
          },
        ]

        return (
          <>
            {hasBreadcrumb && <BreadCrumb items={breadcrumbConfig} />}
            <Render config={uiBuilderConfig} data={uiBuilderData} />
          </>
        )
      } else if (normalContent) {
        return (
          <div>
            <BreadCrumb items={configBreadCrum} />
            <div className="pt-8 sm:pt-16 sm:px-0 relative">
              <div className="container flex flex-col gap-16 relative sm:pb-[7.5rem] pb-16 sm:px-[7.5rem]">
                <div className="flex flex-col gap-6 items-start max-w-[1000px] relative lg:px-0 px-4">
                  <p className="px-2 py-1.5 rounded-lg bg-[#0B9444] uppercase text-white font-semibold text-sm leading-4 inline-block text-center">
                    {tag}
                  </p>
                  <h2 className="text-[#242F3E] font-bold text-5xl leading-[56px]">{titleBlog}</h2>
                  <div
                    dangerouslySetInnerHTML={{ __html: excerpt || "" }}
                    className="text-xl font-normal leading-6 text-[#4F4F4F]"
                  />
                  <div className="flex flex-row gap-16 items-center">
                    <div className="flex flex-col gap-1 items-start">
                      <p className="uppercase text-[#999999] font-normal text-sm leading-5 ">WRITEN BY</p>
                      <p className="text-[#242F3E] font-normal text-base leading-5 ">Manager</p>
                    </div>
                    <div className="flex flex-col gap-1 items-start">
                      <p className="uppercase text-[#999999] font-normal text-sm leading-5 ">PUBLISH ON</p>
                      <p className="text-[#242F3E] font-normal text-base leading-5 ">
                        {dayjs(updatedAtBlog).format("MMM D, YYYY")}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start gap-16 relative">
                  <div className="hidden sm:flex flex-col gap-6 sticky top-24  lg:px-0 px-4">
                    {tableOfContents.length > 0 && (
                      <div className="flex flex-col justify-center gap-4 ">
                        <h2 className="sm:text-sm sm:leading-5 font-normal uppercase text-[#999999]">
                          Table of contents
                        </h2>
                        <TableOfContents items={tableOfContents} />
                      </div>
                    )}
                    {/* <div className="flex flex-row items-center gap-3">
                      <a href="#" className="p-[11px] rounded-full bg-white border border-[#E5E5EA] relative w-10 h-10">
                        <div className="relative w-full h-full">
                          <Image src={IconLink} alt="" fill style={{ objectFit: 'contain' }} />
                        </div>
                      </a>
                      <a href="#" className="p-3 rounded-full bg-white border border-[#E5E5EA] relative w-10 h-10">
                        <div className="relative w-full h-full">
                          <Image src={iconFacebook} alt="" fill style={{ objectFit: 'contain' }} />
                        </div>
                      </a>
                      <a href="#" className="p-3 rounded-full bg-white border border-[#E5E5EA] relative w-10 h-10">
                        <div className="relative w-full h-full">
                          <Image src={iconLinkedin} alt="" fill style={{ objectFit: 'contain' }} />
                        </div>
                      </a>
                      <a href="#" className="p-3 rounded-full bg-white border border-[#E5E5EA] relative w-10 h-10">
                        <div className="relative w-full h-full">
                          <Image src={iconTwitter} alt="" fill style={{ objectFit: 'contain' }} />
                        </div>
                      </a>
                      <a href="#" className="p-3 rounded-full bg-white border border-[#E5E5EA] relative w-10 h-10">
                        <div className="relative w-full h-full">
                          <Image src={IconPinterest} alt="" fill style={{ objectFit: 'contain' }} />
                      </a>
                    </div> */}
                  </div>
                  <div className="flex flex-col gap-16 sm:max-w-[888px] ">
                    <div className="flex flex-col sm:gap-16 gap-10 justify-center border-b border-[#EBEDF0] pb-16">
                      <div className="relative w-full sm:h-[499px] h-[228px]">
                        <Image
                          src={imageUrl ? generateImagePath(imageUrl, "medium") : DefaultImage}
                          alt=""
                          fill
                          className="sm:rounded-2xl object-cover"
                        />
                      </div>
                      <NormalContent content={normalContent} tableOfContents={tableOfContents} />
                    </div>
                    <div className="flex sm:flex-col gap-4 flex-col-reverse px-4 sm:px-0">
                      <div className="w-full flex sm:flex-row flex-col sm:items-center sm:justify-between gap-4">
                        <p className="text-[#999999] uppercase font-normal text-sm">SHARE THIS POST</p>
                        {/* <div className="flex flex-row items-center gap-3">
                          <a href="#" className="p-[11px] rounded-full bg-white border border-[#E5E5EA]">
                            <div className="relative w-6 h-6">
                              <Image src={IconLink} alt="" fill className="object-contain" />
                            </div>
                          </a>
                          <a href="#" className="p-3 rounded-full bg-white border border-[#E5E5EA]">
                            <div className="relative w-6 h-6">
                              <Image src={iconFacebook} alt="" fill className="object-contain" />
                            </div>
                          </a>
                          <a href="#" className="p-3 rounded-full bg-white border border-[#E5E5EA]">
                            <div className="relative w-6 h-6">
                              <Image src={iconLinkedin} alt="" fill className="object-contain" />
                            </div>
                          </a>
                          <a href="#" className="p-3 rounded-full bg-white border border-[#E5E5EA]">
                            <div className="relative w-6 h-6">
                              <Image src={iconTwitter} alt="" fill className="object-contain" />
                            </div>
                          </a>
                          <a href="#" className="p-3 rounded-full bg-white border border-[#E5E5EA]">
                            <div className="relative w-6 h-6">
                              <Image src={IconPinterest} alt="" fill className="object-contain" />
                            </div>
                          </a>
                        </div> */}
                      </div>
                      <div className="flex flex-col gap-1 items-start">
                        <p className="uppercase text-[#999999] font-normal text-sm leading-5 ">WRITEN BY</p>
                        <p className="text-[#242F3E] font-normal text-base leading-5 ">Manager</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#F9F9FB]">
                {dataRelatedPost && (
                  <div className="mx-auto w-full sm:w-auto flex flex-col justify-center gap-10 lg:p-[7.5rem] py-8 px-4 container ">
                    <h2 className="text-5xl leading-[56px] font-semibold">Relatest posts</h2>
                    <div className="grid sm:grid-cols-3 gap-6 sm:gap-10">
                      {dataRelatedPost
                        .filter(item => item.id !== pageData?.[0]?.id)
                        .slice(0, 3)
                        .map((item, index) => {
                          return (
                            <a
                              key={index}
                              href={`/blog/${item?.attributes.slug}`}
                              className="flex flex-col gap-4 lg:max-w-[373.33px] max-w-[343px] lg:mb-6"
                            >
                              <div className="relative h-[270px] lg:w-[373.33px] w-[343px] rounded-2xl">
                                <Image
                                  src={
                                    item?.attributes?.image?.data
                                      ? generateImagePath(item.attributes.image?.data?.attributes?.url, "medium")
                                      : DefaultImage
                                  }
                                  alt="banner"
                                  fill
                                  className="rounded-xl object-cover"
                                  sizes="33vw"
                                />
                              </div>
                              {/* <div className="flex flex-row gap-2 items-center">
                                <div className="flex flex-wrap gap-2">
                                  {item.attributes?.tags &&
                                    item.attributes?.tags.data?.map((tag, index) => (
                                      <button
                                        key={index}
                                        className="py-1 px-2 rounded-md bg-[#EBEDF0] text-center text-[#4F4F4F] flex flex-row gap-1 items-center"
                                      >
                                        <div className="relative w-4 h-4">
                                          <Image src={IconEarth} alt="" fill className="object-contain" />
                                        </div>
                                        {tag.attributes.name}
                                      </button>
                                    ))}
                                </div>

                                {item.attributes.createdAt && (
                                  <p className="text-[#999999] font-normal text-sm" style={{ lineHeight: "19px" }}>
                                    • {dayjs(item.attributes.createdAt).format("MMM D, YYYY")}
                                  </p>
                                )}
                              </div> */}
                              {item.attributes.name && (
                                <p className="font-semibold text-2xl text-[#242F3E]" style={{ lineHeight: "28px" }}>
                                  {item.attributes.name}
                                </p>
                              )}
                              {item.attributes.excerpt && (
                                <div
                                  className="text-[#4F4F4F] text-base font-normal"
                                  style={{ lineHeight: "22px" }}
                                  dangerouslySetInnerHTML={{ __html: item.attributes.excerpt || "" }}
                                />
                              )}
                              <div className="flex flex-row w-full items-center justify-between pb-4 border-b border-[#EBEDF0]">
                                {item.attributes.createdAt && (
                                  <p className="text-[#999999] font-normal text-sm" style={{ lineHeight: "19px" }}>
                                    {dayjs(item.attributes.createdAt).format("DD/MM/YYYY")}
                                  </p>
                                )}

                                <p
                                  className="text-[#0B9444] font-normal text-sm hover:opacity-75 cursor-pointer duration-300"
                                  style={{ lineHeight: "19px" }}
                                >
                                  Read more
                                </p>
                              </div>
                            </a>
                          )
                        })}
                    </div>
                  </div>
                )}
              </div>
              <div
                className=" bg-cover bg-center"
                style={{
                  backgroundImage: `url(${generateImagePath(
                    "/uploads/acdbd925aab637c9a03b34c9289f1dc5_20c7b34be2.webp",
                    "medium",
                  )})`,
                }}
              >
                <div className="relative sm:py-[7.5rem] py-16 sm:px-[220px] px-4 flex lg:flex-row flex-col sm:w-full sm:justify-between gap-10">
                  <div className="absolute inset-0 bg-[#0000001A] z-2"></div>

                  <div className="relative z-3 flex flex-col gap-4">
                    <h2
                      className="text-white font-bold sm:text-5xl text-[32px] sm:leading-[56px] leading-10"
                      style={{
                        textShadow: `0px 2px 3px rgba(0, 0, 0, 0.3), 0px 6px 10px rgba(0, 0, 0, 0.1)`,
                      }}
                    >
                      Leave a Reply
                    </h2>
                    <p className="text-white sm:text-xl text-base leading-[22px]">
                      <span>Your email address will not be published.</span>
                      <br />
                      <span>Required fields are marked *</span>
                    </p>
                  </div>

                  <div className="relative z-3 flex flex-col gap-4 w-full sm:max-w-[463px]">
                    <input type="text" className="py-3 px-4 rounded-xl placeholder-[#8E8E93]" placeholder="Your name" />
                    <input type="text" className="py-3 px-4 rounded-xl placeholder-[#8E8E93]" placeholder="Email" />
                    <input type="text" className="py-3 px-4 rounded-xl placeholder-[#8E8E93]" placeholder="Website" />
                    <textarea
                      className="py-3 px-4 bg-white rounded-xl border border-[#00000014] w-full resize-none placeholder-[#8E8E93]"
                      placeholder="Message"
                      style={{ height: "6rem" }}
                    />
                    <button className="bg-[#0B9444] rounded-xl w-full flex flex-row items-center justify-center gap-2 py-3.5">
                      <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M11.7916 8.9502L8.85059 11.8912"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M17.4419 4.68417L13.5049 16.4942C13.2039 17.3972 11.9729 17.5112 11.5109 16.6792L8.85095 11.8912L4.06294 9.23117C3.23094 8.76917 3.34494 7.53817 4.24794 7.23717L16.0579 3.30017C16.9129 3.01517 17.7269 3.82917 17.4419 4.68417Z"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <p className="text-white font-semibold text-base leading-[22px]">Post Comment</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      } else {
        return <NotFound />
      }
    }
  }
  return (
    <>
      {/* {breadcrumbData && <BreadCrumb items={breadcrumbData} />} */}
      {checkContent()}
    </>
  )
}

export default PageRender
